import { useMemo, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import {
	DiscourseValues,
	useDiscourseContext,
} from '../../Discourse/Services/DiscourseContext';
import {
	useLazyDraftDiscourseQuery,
	useLazyGetUserOriginalIdQuery,
	Document,
} from '../../../graphql/graphql';
import { prepareDefaultValues } from '../../Discourse/Services/discourse.service';
import { useEffectAsync } from '../../../helpers';
import useApiClient from '../../Base/Hooks/useApiClient';
import { DiscourseTextLoadFields } from '../../Discourse/Components/DiscourseTextLoadButton/DiscourseTextLoadButton';

export default function useMedicalReport(patientId: string) {
	const { discourseValues } = useDiscourseContext();
	const [getDraftDiscourseData] = useLazyDraftDiscourseQuery();
	const [getPatientOriginalId] = useLazyGetUserOriginalIdQuery();
	const [discourseData, setDiscourseData] = useState<DiscourseValues>();
	const { displayGQLError } = useApiClient();

	const getDiscourseValuesFromAPI =
		async (): Promise<DiscourseValues | null> => {
			const response = await getPatientOriginalId({ id: patientId }).unwrap();
			displayGQLError(response);

			const originalId = response.user?.originalId;
			if (!originalId) {
				return null;
			}

			const data = await getDraftDiscourseData({
				patientId: originalId,
			}).unwrap();
			displayGQLError(data);

			if (!data.document) {
				return null;
			}

			return prepareDefaultValues(data.document) as DiscourseValues;
		};

	useEffectAsync(async () => {
		if (discourseValues) {
			setDiscourseData(discourseValues);
		} else {
			const data: DiscourseValues | null = await getDiscourseValuesFromAPI();
			if (data) {
				setDiscourseData(data);
			}
		}
	}, []);

	const getTextFromDiscourse = (formMethods: UseFormReturn) => {
		if (discourseData) {
			const diagnoses = [];
			if (
				discourseData.diagnosis &&
				discourseData.diagnosis?.label &&
				discourseData.diagnosis?.value
			) {
				diagnoses.push({
					value: discourseData.diagnosis?.value,
					label: discourseData.diagnosis?.label,
					diagnose: discourseData.diagnosis,
				});
			}
			if (discourseData.diagnoses) {
				discourseData.diagnoses.forEach(item => {
					if (
						item.secondaryDiagnosis?.label &&
						item.secondaryDiagnosis?.value
					) {
						diagnoses.push({
							value: item.secondaryDiagnosis?.value,
							label: item.secondaryDiagnosis?.label,
							diagnose: item.secondaryDiagnosis,
						});
					}
				});
			}
			if (diagnoses.length > 0) {
				formMethods.setValue('diagnoses', diagnoses);
			}
			formMethods.setValue('text', discourseData?.text ?? '');
		}
	};

	const applyMedicalReportFormFromDocument = (
		formMethods: UseFormReturn,
		document: Document,
		fields?: DiscourseTextLoadFields,
	) => {
		const values = prepareDefaultValues(document) as DiscourseValues;

		if (!values) {
			return;
		}

		const {
			text = 'text',
			diagnoses = 'diagnoses',
			diagnosisItemKey,
			diagnosisMode = 'array',
			secondaryDiagnoses,
			secondaryDiagnosisItemKey = 'diagnosis',
		} = fields ?? {};

		const preparedDiagnoses = [];

		if (values.diagnosis?.label && values.diagnosis?.value) {
			preparedDiagnoses.push({
				value: values.diagnosis.value,
				label: values.diagnosis.label,
				diagnose: values.diagnosis,
			});
		}

		values.diagnoses?.forEach(item => {
			if (item.secondaryDiagnosis?.label && item.secondaryDiagnosis?.value) {
				preparedDiagnoses.push({
					value: item.secondaryDiagnosis.value,
					label: item.secondaryDiagnosis.label,
					diagnose: item.secondaryDiagnosis,
				});
			}
		});

		if (text) {
			formMethods.setValue(text, values.text ?? '', {
				shouldDirty: true,
				shouldTouch: true,
				shouldValidate: true,
			});
		}

		if (!diagnoses || preparedDiagnoses.length === 0) {
			return;
		}

		if (diagnosisMode === 'object') {
			const mainDiagnosis = preparedDiagnoses[0];

			formMethods.setValue(
				diagnosisItemKey ? `${diagnoses}.${diagnosisItemKey}` : diagnoses,
				mainDiagnosis.diagnose,
				{
					shouldDirty: true,
					shouldTouch: true,
					shouldValidate: true,
				},
			);

			if (secondaryDiagnoses) {
				const secondaryItems = preparedDiagnoses.slice(1);

				if (secondaryItems.length > 0) {
					formMethods.setValue(
						secondaryDiagnoses,
						secondaryItems.map(item => ({
							[secondaryDiagnosisItemKey]: item.diagnose,
						})),
						{
							shouldDirty: true,
							shouldTouch: true,
							shouldValidate: true,
						},
					);
				}
			}

			return;
		}

		formMethods.setValue(
			diagnoses,
			diagnosisItemKey
				? preparedDiagnoses.map(item => ({
						[diagnosisItemKey]: item.diagnose,
				  }))
				: preparedDiagnoses,
			{
				shouldDirty: true,
				shouldTouch: true,
				shouldValidate: true,
			},
		);
	};

	const canLoadFromDiscourse = useMemo(() => {
		return !!discourseData;
	}, [discourseData]);

	return {
		getTextFromDiscourse,
		canLoadFromDiscourse,
		applyMedicalReportFormFromDocument,
	};
}
